import type { Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { Button, type ButtonOptions } from "./Button.js";

export interface GlyphButtonOptions extends ButtonOptions {
  /** Name from the glyph registry — "close", "plus", "chevron-down"… */
  glyph?: string;
  /**
   * How much of the button the glyph fills, 0..1 of the smaller side.
   * The default leaves a comfortable margin at toolbar sizes.
   */
  glyphScale?: number;
  /** Stroke weight. Derived from the glyph's size when omitted. */
  weight?: number;
}

/** Fraction of the smaller side the glyph takes by default. */
const DEFAULT_SCALE = 0.55;

/**
 * A button whose face is a glyph rather than a caption.
 *
 * The point of a separate control rather than a `Button` with an icon is that
 * nothing here is a font or an image: glyphs are drawn from the renderer's own
 * primitives, so they stay sharp at any size and pick up the theme's ink
 * without an asset pipeline. Give it a `tooltip` — a button with no words on
 * it needs one.
 *
 * ```ts
 * new GlyphButton({ glyph: "close", tooltip: "Close", onClick: () => win.close() });
 * ```
 *
 * `registerGlyph` adds more; an unknown name draws nothing rather than
 * throwing, so a missing glyph is a blank button and not a broken frame.
 */
export class GlyphButton extends Button {
  glyph: string;
  glyphScale: number;
  weight: number | null;

  constructor(options: GlyphButtonOptions = {}) {
    super({ width: 32, height: 32, ...options });
    this.glyph = options.glyph ?? "";
    this.glyphScale = options.glyphScale ?? DEFAULT_SCALE;
    this.weight = options.weight ?? null;
  }

  /** The square the glyph is drawn in, centered on the button's face. */
  glyphRect(pressed = false): Rect {
    const r = this.localRect;
    const side = Math.max(2, Math.round(Math.min(r.width, r.height) * this.glyphScale));
    const nudge = pressed ? 1 : 0;
    return {
      x: Math.round(r.x + (r.width - side) / 2) + nudge,
      y: Math.round(r.y + (r.height - side) / 2) + nudge,
      width: side,
      height: side,
    };
  }

  /** Stroke weight for the current size, unless one was set explicitly. */
  glyphWeight(box: Rect): number {
    return this.weight ?? Math.max(1, Math.round(Math.min(box.width, box.height) / 10));
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state: VisualState = this.visualState();
    theme.drawButton(rc, this.localRect, state);
    if (!this.glyph) return;
    const box = this.glyphRect(state.pressed);
    paintGlyph(this.glyph, {
      rc,
      box,
      ink: theme.buttonTextColor(state),
      weight: this.glyphWeight(box),
    });
  }
}
